/**
 * What a guest's QR code carries after "#" (2026-10-24): their name and the event's, short, so
 * the RSVP page can greet them while the server keeps neither (invite.tsx, pages/rsvp.tsx). A
 * browser never sends the part of an address after "#" to a server. Kept short - a longer code
 * has smaller squares, harder to print and scan at 22 mm.
 */

/**
 * At most this many bytes of the guest's name, and of the event's, in UTF-8: about 20 and 24
 * letters in English, half that in Arabic or Russian (two bytes a letter). The whole link then
 * stays at most 37 squares across (qr.ts), each over half a millimetre at 22 mm.
 */
export const HASH_NAME_MAX = 20;
export const HASH_TITLE_MAX = 24;

const enc = new TextEncoder();
const toBase64Url = (bytes: Uint8Array) => {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** A name cut to at most `max` bytes: whole letters (never half a letter or an emoji), and whole words when it has several. */
function cut(s: string, max: number): string {
  const whole = s.trim();
  let out = '';
  for (const ch of whole) {
    if (enc.encode(out + ch).length > max) break;
    out += ch;
  }
  if (out.length < whole.length && !/\s/.test(whole[out.length]) && /\s/.test(out)) out = out.slice(0, out.search(/\s\S*$/));
  return out.trim();
}

export const inviteHash = (name: string, title: string) =>
  toBase64Url(enc.encode(`${cut(name, HASH_NAME_MAX)}\n${cut(title, HASH_TITLE_MAX)}`));

/** The guest's and the event's names from a code's "#", or null when there are none. */
export function readInviteHash(hash: string): { name: string; title: string } | null {
  try {
    const b = hash.replace(/^#/, '').replace(/-/g, '+').replace(/_/g, '/');
    if (!b) return null;
    const bin = atob(b + '='.repeat((4 - (b.length % 4)) % 4));
    const [name = '', title = ''] = new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(bin, (c) => c.charCodeAt(0))).split('\n');
    return name.trim() || title.trim() ? { name: name.trim(), title: title.trim() } : null;
  } catch {
    return null;
  }
}
